import { AttributesMixin, define, Model, type } from "@type-r/models";
import { CompanyItem } from "./company";
import { getAuthorizationBearer, getPlatformCode, restfulIO } from "./endpoint";

export interface RuleItem extends AttributesMixin<typeof RuleItem> { }
@define
export class RuleItem extends Model {
    static attributes = {
        id: type(String),
        fieldId: type(Number),
        fieldName: type(String),
        isRequired: type(Boolean),
        operatorOption: type(Number),
        value: type(String),
        errorMessage: type(String)
    }
}

export interface DuplicationCheck extends AttributesMixin<typeof DuplicationCheck> { }
@define
export class DuplicationCheck extends Model {
    static attributes = {
        fieldId: type(Number),
        fieldName: type(String)
    }
}

export interface ValidationRuleDetails extends AttributesMixin<typeof ValidationRuleDetails> { }
@define
export class ValidationRuleDetails extends Model {
    static endpoint = restfulIO('api/v1/ingest/validation-rules');
    static attributes = {
        id: type(String).value(null),
        name: type(String).value(null).required,
        assetTypeId: type(Number).value(null).required,
        isActive: type(Boolean).value(false),
        companies: type([CompanyItem]).value([]).required,
        ruleSets: type([RuleItem]).value([]),
        duplicationChecks: type([DuplicationCheck]).value([])
    }

    buildFormData() {
        const formData = new FormData();
        formData.append('id', this.defaultValue(this.id));
        formData.append('name', this.defaultValue(this.name));
        formData.append('assetTypeId', this.defaultValue(this.assetTypeId));
        formData.append('isActive', this.defaultValue(this.isActive) ? 'True' : 'False');
        this.companies.forEach((item, index) => {
            formData.append(`companies[${index}].id`, item.id);
            formData.append(`companies[${index}].name`, item.name);
        });
        this.ruleSets.forEach((item, index) => {
            if (item.fieldId && item.fieldName) 
            {
                formData.append(`ruleSets[${index}].fieldId`, this.defaultValue(item.fieldId));
                formData.append(`ruleSets[${index}].fieldName`, this.defaultValue(item.fieldName));
                formData.append(`ruleSets[${index}].isRequired`, this.defaultValue(item.isRequired) ? 'True' : 'False');
                formData.append(`ruleSets[${index}].value`, this.defaultValue(item.value));
                formData.append(`ruleSets[${index}].errorMessage`, this.defaultValue(item.errorMessage));
                formData.append(`ruleSets[${index}].operatorOption`, this.defaultValue(1));
            }
        });
        this.duplicationChecks.forEach((item, index) => {
            if (item.fieldId && item.fieldName) 
            { 
                formData.append(`duplicationChecks[${index}].fieldId`, this.defaultValue(item.fieldId));
                formData.append(`duplicationChecks[${index}].fieldName`, this.defaultValue(item.fieldName));
            }
        });
        return formData;
    }

    defaultValue(value: any) {
        return Boolean(value) ? value.toString() : '';
    }

    async get(id: string): Promise<ValidationRuleDetails> {
        const url = `api/v1/ingest/validation-rules/${id}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                "Authorization": getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            }),
        });

        if (response.status < 200 || response.status >= 300) {
            let error = response.statusText || `HTTP Error ${response.status}`;
            await response.json()
                .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
                .catch(e => console.error('Cannot parse response data'));
            throw new Error(error);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }

    async getStandard(id: string): Promise<ValidationRuleDetails> {
        const url = `api/v1/ingest/validation-rules/standard/${id}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: new Headers({
                "Authorization": getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            }),
        });

        if (response.status < 200 || response.status >= 300) {
            let error = response.statusText || `HTTP Error ${response.status}`;
            await response.json()
                .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
                .catch(e => console.error('Cannot parse response data'));
            throw new Error(error);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }

    async save() {
        const url = (this.getEndpoint() as any).getUrl(this);
        const httpVerb = this.id ? 'PUT' : 'POST';

        const response = await fetch(url, {
            method: httpVerb,
            headers: new Headers({
                'Authorization': getAuthorizationBearer(),
                'X-Platform-Code': getPlatformCode()
            }),
            body: this.buildFormData()
        });

        if (response.status < 200 || response.status >= 300) {
            let error = response.statusText || `HTTP Error ${response.status}`;
            await response.json()
                .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
                .catch(e => console.error('Cannot parse response data'));
            throw new Error(error);
        }

        const json = await response.json();
        this.set(json);
        return this;
    }
}

